import { Button, DatePicker, Form, Select, notification } from 'antd'

import { useEffect, useState } from 'react'

import './TaskDrawer.style.less'
import TextList from './TextList'
import FooterForm from './FooterForm'
import {
  TASK_STATUS,
  TASK_STATUS_ALL,
  TASK_STATUS_UPDATER,
} from '@/screens/Order/Pages/Landing/components/ModalMassUpdate/Task.constant'
import { assign, get, set } from 'lodash'
import { useUpdateTaskDetail } from '../TaskTable.hook'
import { PlanStatuses } from '@/screens/Order/Pages/OrderOverview/components/TimeAndAction/components/DetailSection/components/TaskTemplate/Fields/StatusField/StatusField.enum'
import { isEmptyValue } from '@/utils/common'
import moment from 'moment'
import 'moment-timezone'
import {
  DATE_FORMAT,
  convertToPercentageValue,
  convertUtcToLocalDisplay,
  getKeyByValue,
} from '../TaskTable.transform'
import Input from 'antd/lib/input/Input'
import { ActionField } from './ActionField/ActionField'
import { useAuth } from '@/providers/auth.provider'
import { dateFormat } from '@/utils/date'
import { DATE_FORMAT as DATE_FORMAT_CONST } from '@/constant/date'

type StatusActualDateFormTask = {
  value?: string | number
  assignee?: { id?: string | number }
  order?: {
    assignments?: {
      user?: { id?: string | number }
      roles?: { role?: string }
    }[]
  }
}

type StatusActualDateFormProps = {
  id: string | number
  taskType: string
  status?: string | number
  value?: string
  actualDate?: string
  plan_start_date?: string
  progress_status?: string
  canEdit?: boolean
  data: StatusActualDateFormTask
  refetch: () => void
  onUpdated: (value: any) => void
}

function StatusActualDateForm(props: StatusActualDateFormProps) {
  const { value, plan_start_date, refetch, data, onUpdated } = props
  const { status = 0, taskType, canEdit = false } = props
  const { id, actualDate, progress_status } = props

  const [form] = Form.useForm()
  const [onEdit, setOnEdit] = useState(false)

  const { user } = useAuth()

  const isUserAssignedAndIsUpdater = () => {
    const isCurrentUserAssign = data.assignee.id === user?.id
    const isUserPartOfCollaboration = data?.order?.assignments?.find(
      (assignment) => {
        return assignment?.user?.id === user?.id
      }
    )
    return (
      isCurrentUserAssign &&
      isUserPartOfCollaboration &&
      isUserPartOfCollaboration.roles.role === 'updater'
    )
  }

  const options = get(
    isUserAssignedAndIsUpdater() ? TASK_STATUS_UPDATER : TASK_STATUS,
    taskType,
    {}
  )
  const {
    mutate,
    isLoading,
    isSuccess,
    data: updatedData,
    isError,
    error,
  } = useUpdateTaskDetail()

  const [percentageValue, setPercentageValue] = useState(status)

  useEffect(() => {
    setPercentageValue(status)
  }, [status])

  const resetForms = () => {
    form.resetFields()
  }

  useEffect(() => {
    resetForms()
  }, [taskType])

  useEffect(() => {
    if (updatedData?.data) {
      onUpdated(updatedData?.data)
    }
  }, [updatedData])

  useEffect(() => {
    if (isError) {
      notification.error({
        message:
          (error as any)?.response?.data?.message ||
          'Updating Status and Actual Date error.',
        duration: 4,
        placement: 'top',
      })
    }
  }, [isError, updatedData])

  useEffect(() => {
    if (!isSuccess) return
    notification.success({
      message: 'Updated Status and Actual Date successfully.',
      duration: 4,
      placement: 'top',
    })
    handleCancel()
    refetch()
  }, [isSuccess])

  const onUpdatePlan = async (newStatus: string, actualDate: any) => {
    let task = {}
    let status: any = newStatus

    if (isEmptyValue(status)) {
      if (value === newStatus) {
        set(task, 'value', 'planned')
        if (taskType !== 'percentage_progress_type')
          set(task, 'activity_value', 'planned')
        else set(task, 'activity_value', 'planned')
      } else if (
        status === PlanStatuses.APPROVED ||
        status === PlanStatuses.DONE ||
        status === PlanStatuses.PASS ||
        status === PlanStatuses.FAIL ||
        status === PlanStatuses.NOT_APPLICABLE
      ) {
        set(task, 'progress_status', 'done')
      } else {
        set(task, 'progress_status', 'in_progress')
      }
    } else {
      if (status === 'planned') {
        set(task, 'progress_status', 'in_progress')
      } else if (
        parseInt(status) === 100 ||
        status === PlanStatuses.APPROVED ||
        status === PlanStatuses.DONE ||
        status === PlanStatuses.PASS ||
        status === PlanStatuses.FAIL ||
        status === PlanStatuses.NOT_APPLICABLE
      ) {
        set(task, 'progress_status', 'done')
      } else {
        set(task, 'progress_status', 'in_progress')
      }
    }

    if (status === 'planned') set(task, 'value', 'planned')
    else set(task, 'value', `${status}`)
    set(
      task,
      'json_value.plan_date',
      dateFormat(
        moment(plan_start_date).toISOString(),
        DATE_FORMAT_CONST.YYYYMMDD
      )
    )
    set(task, 'json_value.current_status', status)
    set(task, 'activity_value', status)
    set(
      task,
      'actual_date',
      dateFormat(
        actualDate?.toISOString?.() || actualDate,
        DATE_FORMAT_CONST.YYYYMMDD
      )
    )

    mutate({
      payload: task,
      id,
      type: 'status',
    })
  }

  const onFinish = (values: any) => {
    onUpdatePlan(
      values.newStatus,
      (values.actualDate || moment()).format(DATE_FORMAT_CONST.YYYYMMDD)
    )
  }

  const handleCancel = () => {
    setOnEdit(false)
    resetForms()
  }

  // for percentage status
  if (taskType === 'percentage_progress_type') {
    const handleChange = (event) => {
      let newValue = event.target.value
      setPercentageValue(convertToPercentageValue(newValue))
    }

    const onSave = () => {
      const fallbackActualDate =
        actualDate || moment().format(DATE_FORMAT_CONST.YYYYMMDD)
      if (percentageValue < data.value) {
        notification.error({
          message: `Value must be greater than previous value (${data.value})%`,
          duration: 4,
          placement: 'bottom',
        })
        return
      }
      onUpdatePlan(`${percentageValue}`, fallbackActualDate)
    }

    if (progress_status === 'new') {
      return <TextList label="Status" value={'-'} isEditable={false} />
    }

    if (parseInt(updatedData?.data?.tasks?.value) >= 100) {
      return (
        <TextList
          label="Status"
          value={updatedData?.data?.tasks?.value + '%'}
          isEditable={false}
        />
      )
    }

    if (Number(status) >= 100 || !canEdit) {
      return (
        <TextList
          label="Status"
          value={`${parseInt(String(status)) || 0}` + '%'}
          isEditable={false}
        />
      )
    }

    return (
      <div className="percentage-form">
        <TextList label="Status">
          <Input
            value={parseInt(String(percentageValue)) || 0}
            onChange={handleChange}
            suffix={'%'}
          />
        </TextList>
        <Button
          type="primary"
          size="middle"
          htmlType="submit"
          className="button-approve"
          loading={isLoading}
          onClick={onSave}
        >
          Save
        </Button>
      </div>
    )
  }
  // end percentage status
  const onClickActions = (value) => {
    setOnEdit(true)
    form.setFieldValue('newStatus', value)
    form.setFieldValue('fromAction', true)
  }

  const transformedOptions = Object.entries(options).map(([key, value]) => {
    return {
      value: value,
      label: key,
    }
  })

  if (onEdit) {
    return (
      <div className="task-form">
        <Form
          form={form}
          onFinish={onFinish}
          layout="horizontal"
          labelCol={{ span: 10 }}
          wrapperCol={{ span: 14 }}
          colon={false}
        >
          <Form.Item label="Status" name="newStatus">
            <Select
              placeholder="Select"
              size="middle"
              options={transformedOptions}
              disabled={form.getFieldValue('fromAction')}
            />
          </Form.Item>
          <Form.Item
            label="Actual Date"
            name="actualDate"
            initialValue={moment()}
          >
            <DatePicker placeholder="Date" size="middle" format={DATE_FORMAT} />
          </Form.Item>
          <FooterForm isLoading={isLoading} handleCancel={handleCancel} />
        </Form>
      </div>
    )
  }

  const checkEditable = () => {
    if (status === 'pass_fail_type_planned') {
      return value !== 'pass' && value !== 'fail'
    }
    if (status === 'done') return false
    if (['submitted'].includes(String(status)) && isUserAssignedAndIsUpdater())
      return false
    return canEdit
  }
  const displayStatus =
    getKeyByValue(options, String(status)) ||
    getKeyByValue(TASK_STATUS_ALL, String(status)) ||
    'Not Planned'

  return (
    <>
      <TextList
        onClickEdit={() => setOnEdit(true)}
        label="Status"
        value={displayStatus}
        isEditable={checkEditable()}
      />
      <TextList
        label="Actual Date"
        value={
          convertUtcToLocalDisplay(
            updatedData?.data?.tasks?.actual_date || actualDate
          ) || '-'
        }
        isEditable={false}
      />

      {canEdit && (
        <ActionField
          onSubmit={onClickActions}
          current_status={updatedData?.data?.tasks?.value || status}
          type={taskType}
          value={updatedData?.data?.tasks?.value}
          isUserssignedAndIsUpdater={isUserAssignedAndIsUpdater()}
        />
      )}
    </>
  )
}

export default StatusActualDateForm
